import Box from "@ui-kit/layout/Box/Box.web";
import Flex from "@ui-kit/layout/Flex/Flex.web";
import { useCallback, useEffect, useState } from "react";
import { DateRange } from "react-day-picker";

import DatePicker from "@ui-kit/form/DatePicker/DatePicker.web";
import DatePickerQuickOptions from "@ui-kit/form/DatePickerQuickOptions/DatePickerQuickOptions.web";
import {
  DatePickerMode,
  DatePickerSelection,
  DatePickerSelectionEvent,
} from "@ui-kit/form/DatePicker/DatePicker.props";
import { DatePickerRangeContentProps } from "./DatePickerRangeContent.props";

/**
 * A reusable date range picker content component that combines
 * the quick options menu and calendar.
 * Use this to embed date selection inside modals/containers.
 */
const DatePickerRangeContent: React.FC<DatePickerRangeContentProps> = ({
  currentStartDate,
  currentEndDate,
  onDatesChange,
  onRangeComplete,
  selectPastDates = false,
  numberOfMonths = 1,
  showQuickMenu = true,
  customQuickMenuOptions,
  compact = true,
  flexDirection = "column",
}) => {
  const [selected, setSelected] = useState<DatePickerSelection | undefined>(
    () => {
      if (currentStartDate || currentEndDate) {
        return {
          from: currentStartDate ?? undefined,
          to: currentEndDate ?? undefined,
        };
      }
      return undefined;
    }
  );

  const [month, setMonth] = useState<Date>(
    () => currentStartDate ?? new Date()
  );

  // Sync external props with internal state
  useEffect(() => {
    if (currentStartDate || currentEndDate) {
      setSelected({
        from: currentStartDate ?? undefined,
        to: currentEndDate ?? undefined,
      });
      if (currentStartDate) {
        setMonth(currentStartDate);
      }
    } else {
      setSelected(undefined);
    }
  }, [currentStartDate, currentEndDate]);

  const handleCalendarDateChange = useCallback(
    (event: DatePickerSelectionEvent) => {
      const range = event.selected as DateRange | undefined;
      setSelected(range);

      const newStartDate = range?.from ?? null;
      const newEndDate = range?.to ?? null;

      onDatesChange({
        startDate: newStartDate,
        endDate: newEndDate,
      });

      if (event.isRangeComplete && newStartDate && newEndDate) {
        onRangeComplete?.(newStartDate, newEndDate);
      }
    },
    [onDatesChange, onRangeComplete]
  );

  const handleQuickMenuChange = useCallback(
    (event: DatePickerSelectionEvent) => {
      const range = event.selected as DateRange | undefined;
      setSelected(range);

      const newStartDate = range?.from ?? null;
      const newEndDate = range?.to ?? null;

      onDatesChange({
        startDate: newStartDate,
        endDate: newEndDate,
      });

      if (newStartDate && newEndDate) {
        onRangeComplete?.(newStartDate, newEndDate);
      }
    },
    [onDatesChange, onRangeComplete]
  );

  return (
    <Flex
      flexDirection={{ base: "column", md: flexDirection }}
      width={flexDirection === "row" ? "max-content" : "100%"}
      data-testid="date-picker-range-content"
    >
      {showQuickMenu && (
        <Box
          flex="0 0 auto"
          borderRight={{
            base: "none",
            md: "1px solid var(--chakra-colors-border)",
          }}
          borderBottom={{
            base: "none",
            sm: "1px solid var(--chakra-colors-border)",
            md: "none",
          }}
        >
          <DatePickerQuickOptions
            setMonth={setMonth}
            setSelection={setSelected}
            mode={DatePickerMode.range}
            onChangeSelected={handleQuickMenuChange}
            customMenuOptions={customQuickMenuOptions}
          />
        </Box>
      )}

      <Flex
        flex="1"
        padding={compact ? "md" : "lg"}
        justifyContent="center"
        alignItems="flex-start"
        flexDirection={flexDirection}
      >
        <DatePicker
          mode={DatePickerMode.range}
          selected={selected as DateRange}
          onChangeSelected={handleCalendarDateChange}
          month={month || (selected as DateRange)?.from || new Date()}
          onMonthChange={setMonth}
          disablePastDates={!selectPastDates}
          numberOfMonths={numberOfMonths}
          data-testid="date-picker-range-calendar"
        />
      </Flex>
    </Flex>
  );
};

export default DatePickerRangeContent;
